import React, { useState, useEffect } from 'react';
import { X, Save, FileText, Terminal, Shield } from 'lucide-react';

const scriptOptions = [
  {
    value: 'powershell',
    label: 'PowerShell',
    placeholder: '# Write your PowerShell script here',
    iconName: 'terminal',
    color: 'text-blue-600'
  },
  {
    value: 'bash',
    label: 'Bash',
    placeholder: '#!/bin/bash',
    iconName: 'terminal',
    color: 'text-orange-600'
  },
  {
    value: 'shell',
    label: 'Shell',
    placeholder: '#!/bin/sh',
    iconName: 'terminal',
    color: 'text-slate-600'
  },
  {
    value: 'cmd',
    label: 'CMD',
    placeholder: 'REM Windows CMD script',
    iconName: 'terminal',
    color: 'text-gray-600'
  },
  {
    value: 'sql',
    label: 'SQL',
    placeholder: 'SELECT * FROM devices;',
    iconName: 'file',
    color: 'text-purple-600'
  }
];

const riskLevels = ['Low', 'Medium', 'High', 'Critical'];

const PolicyBuilderModal = ({ isOpen, deviceType, baselineType, onClose, onSave }) => {
  const [formState, setFormState] = useState({
    title: '',
    description: '',
    scriptType: 'powershell',
    risk: 'Medium',
    content: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFormState({
        title: '',
        description: '',
        scriptType: deviceType === 'linux' ? 'bash' : deviceType === 'macos' ? 'shell' : 'powershell',
        risk: 'Medium',
        content: ''
      });
      setError('');
    }
  }, [isOpen, deviceType]);

  if (!isOpen) {
    return null;
  }

  const currentScriptMeta = scriptOptions.find((opt) => opt.value === formState.scriptType) || scriptOptions[0];

  const handleChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!formState.title.trim()) {
      setError('Policy name is required.');
      return;
    }
    if (!formState.content.trim()) {
      setError('Script content cannot be empty.');
      return;
    }

    const payload = {
      id: `custom-policy-${Date.now()}`,
      title: formState.title.trim(),
      description: formState.description.trim() || 'Custom policy created from dashboard.',
      deviceType,
      baselineType: baselineType || null,
      lang: currentScriptMeta.label,
      commandType: formState.scriptType,
      risk: formState.risk,
      iconName: currentScriptMeta.iconName,
      color: currentScriptMeta.color,
      content: formState.content,
      type: formState.scriptType === 'sql' ? 'command' : 'script',
      createdAt: new Date().toISOString(),
      isCustom: true
    };

    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400">
              {baselineType ? 'Baseline Policy Builder' : 'Global Policy Builder'}
            </p>
            <h2 className="text-xl font-bold text-slate-900">Create Custom Policy</h2>
          </div>
          <button className="text-slate-500 hover:text-slate-800" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="space-y-6 px-6 py-6">
          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Policy Name</label>
                <input
                  type="text"
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="e.g. Harden Windows Firewall"
                  value={formState.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Script Type</label>
                <select
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm capitalize focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={formState.scriptType}
                  onChange={(e) => handleChange('scriptType', e.target.value)}
                >
                  {scriptOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Risk Level</label>
                <select
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={formState.risk}
                  onChange={(e) => handleChange('risk', e.target.value)}
                >
                  {riskLevels.map((level) => (
                    <option key={level}>{level}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Description</label>
                <textarea
                  className="mt-2 h-28 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Describe what this policy enforces..."
                  value={formState.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">
                  {currentScriptMeta.label} Script
                </label>
                {formState.scriptType === 'sql' ? (
                  <FileText size={16} className="text-purple-500" />
                ) : (
                  <Terminal size={16} className="text-blue-500" />
                )}
              </div>
              <textarea
                className="h-64 w-full rounded-lg border border-slate-300 bg-slate-900 p-4 font-mono text-sm text-green-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder={currentScriptMeta.placeholder}
                value={formState.content}
                onChange={(e) => handleChange('content', e.target.value)}
                spellCheck={false}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 hover:bg-blue-700"
              onClick={handleSave}
            >
              <Save size={16} />
              Save Policy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyBuilderModal;


